
import React, { useEffect, useState, useRef, useMemo } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Modal, Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import SectionLoader from "../../../components/SectionLoader";
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import axios from "axios";
import JoditEditor from "jodit-react";
import { error, success } from "../../../notify";

const CustomizeChat = () => {
    const [customizedChats, setCustomizedChats] = useState([]);
    const [selectedChat, setSelectedChat] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showBotResponseModal, setShowBotResponseModal] = useState(false);
    const [botResponse, setBotResponse] = useState("");
    const [isFetchingBotResponse, setIsFetchingBotResponse] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [editQuery, setEditQuery] = useState("");
    const [editResponse, setEditResponse] = useState("");
    const [addQuery, setAddQuery] = useState("");
    const [addResponse, setAddResponse] = useState("");
    const [addResponseReady, setAddResponseReady] = useState(false);
    const [editResponseReady, setEditResponseReady] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [itemsPerPage, setItemsPerPage] = useState(() => {
        return sessionStorage.getItem("current_page_limit")
            ? parseInt(sessionStorage.getItem("current_page_limit"), 10)
            : 50;
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [currentRangeStart, setCurrentRangeStart] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [searchInputValue, setSearchInputValue] = useState("");
    const [showClearButton, setShowClearButton] = useState(false);

    const offset = useRef(0);
    const isInitialPageLimitSet = useRef(false);
    const botResponseButtonRef = useRef(null);
    const addResponseRef = useRef("");
    const editResponseRef = useRef("");
    const pagesToShow = 10;
    const getApi = `${process.env.REACT_APP_SERVER_URL}/api/website/customization`;
    const getBotResponseApi = `${process.env.REACT_APP_SERVER_URL}/api/website/customization/get_bot_response`;
    const setPageLimitURL = `${process.env.REACT_APP_SERVER_URL}/api/set-page-limit/`;
    const token = sessionStorage.getItem("revizeTrainingToken");
    const location = useLocation();
    const { siteId, siteName } = location.state || {};

    const navigate = useNavigate();

    // Jodit editor configuration
    const joditConfig = useMemo(
        () => ({
            readonly: false,
            buttonsMD: ["bold", "italic", "|", "ul", "ol", "link", "image"],
            buttonsSM: ["bold", "italic", "|", "ul", "ol", "link", "image"],
            buttonsXS: ["bold", "italic", "|", "ul", "ol", "link", "image"],
            statusbar: false,
            link: {
                openInNewTabCheckbox: true,
                noFollowCheckbox: false,
            },
        }),
        []
    );

    const joditPlainText = (html) =>
        String(html || "")
            .replace(/<[^>]*>/g, "")
            .replace(/&nbsp;/gi, " ")
            .trim();

    const syncEditorContent = (html, mode) => {
        const ready = !!joditPlainText(html);
        if (mode === "add") {
            addResponseRef.current = html || "";
            setAddResponseReady((prev) => (prev === ready ? prev : ready));
        } else {
            editResponseRef.current = html || "";
            setEditResponseReady((prev) => (prev === ready ? prev : ready));
        }
    };

    // Icons (same as in AllowedExternalWebsites.js)
    const EditIcon = () => (
        <svg width="16" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M13.7476 20.4428H21.0002" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path fillRule="evenodd" clipRule="evenodd" d="M12.78 3.79479C13.5557 2.86779 14.95 2.73186 15.8962 3.49173C15.9485 3.53296 17.6295 4.83879 17.6295 4.83879C18.669 5.46719 18.992 6.80311 18.3494 7.82259C18.3153 7.87718 8.81195 19.7645 8.81195 19.7645C8.49578 20.1589 8.01583 20.3918 7.50291 20.3973L3.86353 20.443L3.04353 16.9723C2.92866 16.4843 3.04353 15.9718 3.3597 15.5773L12.78 3.79479Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M11.021 6.00098L16.4732 10.1881" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );

    const DeleteIcon = () => (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19.3248 9.46826C19.3248 9.46826 18.7818 16.2033 18.4668 19.0403C18.3168 20.3953 17.4798 21.1893 16.1088 21.2143C13.4998 21.2613 10.8878 21.2643 8.27979 21.2093C6.96079 21.1823 6.13779 20.3783 5.99079 19.0473C5.67379 16.1853 5.13379 9.46826 5.13379 9.46826" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M20.708 6.23975H3.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M17.4406 6.23973C16.6556 6.23973 15.9796 5.68473 15.8256 4.91573L15.5826 3.69973C15.4326 3.13873 14.9246 2.75073 14.3456 2.75073H10.1126C9.53358 2.75073 9.02558 3.13873 8.87558 3.69973L8.63258 4.91573C8.47858 5.68473 7.80258 6.23973 7.01758 6.23973" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );

    const RefreshIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#5a6acf" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 2v6h6"></path>
            <path d="M21 12a9 9 0 0 0-15-6.7L3 8"></path>
            <path d="M21 22v-6h-6"></path>
            <path d="M3 12a9 9 0 0 0 15 6.7L21 16"></path>
        </svg>
    );

    const BackIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="23" height="23" viewBox="0 0 24 24" fill="none" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"></polyline>
            <line x1="5" y1="6" x2="5" y2="18"></line>
        </svg>
    );

    const SearchIcon = () => (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" />
        </svg>
    );

    const ClearIcon = () => (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
    );

    // Search functionality - only search when button is clicked
    const handleSearch = async () => {
        if (searchInputValue.trim() !== "") {
            setShowClearButton(true);
        }
        setCurrentPage(1);
        setCurrentRangeStart(1);
        const newOffset = 0;
        offset.current = newOffset;
        await fetchcustomizedChats(newOffset, searchInputValue);
    };

    const handleClearSearch = async () => {
        setSearchInputValue("");
        setShowClearButton(false);
        setCurrentPage(1);
        setCurrentRangeStart(1);
        const newOffset = 0;
        offset.current = newOffset;
        await fetchcustomizedChats(newOffset, "");
    };

    // Refresh functionality
    const handleRefresh = async () => {
        const newOffset = (currentPage - 1) * itemsPerPage;
        offset.current = newOffset;
        await fetchcustomizedChats(newOffset, searchInputValue);
        success("Customized responses refreshed successfully.", true);
    };

    useEffect(() => {
        const updatePageLimit = async () => {
            const headers = {
                "accept": "application/json",
                "Authorization": "Bearer " + token
            };
            const loggedInUserId = sessionStorage.getItem("loggedInUserId");

            if (!isInitialPageLimitSet.current) {
                isInitialPageLimitSet.current = true;
                return;
            }
            if (itemsPerPage != null) {
                try {
                    await axios.post(setPageLimitURL,
                        { page_limit: itemsPerPage },
                        {
                            headers: headers,
                            params: { user_id: loggedInUserId }
                        }
                    );
                    sessionStorage.setItem('current_page_limit', itemsPerPage);
                    sessionStorage.setItem('previous_page_limit', itemsPerPage);
                } catch (setLimitErr) {
                    console.error('Error setting page limit:', setLimitErr);
                }
            }
        };
        updatePageLimit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemsPerPage]);

    // Fetch customized records with search functionality
    const fetchcustomizedChats = async (offset, searchText = "") => {
        setIsLoading(true);
        try {
            let url = `${getApi}/${siteId}?offset=${offset}&limit=${itemsPerPage}`;

            // Add search filter if provided
            if (searchText.trim() !== "") {
                url += `&search_text=${encodeURIComponent(searchText.trim())}`;
            }

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });

            const data = await response.json();
            setCustomizedChats(data.queries || []);
            if (data.total_count !== undefined) {
                setTotalCount(data.total_count);
            }
            if (data.totalPages !== undefined) {
                setTotalPages(data.totalPages);
            }
        } catch (err) {
            console.error("Error fetching customized records:", err);
            setCustomizedChats([]);
            setTotalCount(0);
            setTotalPages(1);
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch bot response
    const fetchBotResponse = async () => {
        if (!siteId || !editQuery.trim()) {
            error("Website ID or query is missing", true);
            return;
        }

        try {
            setIsFetchingBotResponse(true);
            const response = await axios.get(
                `${getBotResponseApi}/${siteId}`,
                {
                    headers: {
                        "accept": "application/json",
                        "Authorization": "Bearer " + token
                    },
                    params: {
                        query: editQuery
                    }
                }
            );
            setBotResponse(response.data || "No response received");
            setShowBotResponseModal(true);
        } catch (err) {
            console.error("Error fetching bot response:", err);
            error(err.response?.data?.detail || "Failed to fetch bot response", true);
        } finally {
            setIsFetchingBotResponse(false);
        }
    };

    useEffect(() => {
        const newOffset = (currentPage - 1) * itemsPerPage;
        offset.current = newOffset;
        fetchcustomizedChats(newOffset, searchInputValue);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, itemsPerPage]);

    // Position the bot response modal
    useEffect(() => {
        const updateModalPosition = () => {
            if (showBotResponseModal && botResponseButtonRef.current) {
                const buttonRect = botResponseButtonRef.current.getBoundingClientRect();
                const modal = document.querySelector('.bot-response-modal');
                if (modal) {
                    modal.style.position = 'fixed';
                    modal.style.top = `${buttonRect.top - 45}px`;
                    modal.style.left = `${buttonRect.right + 10}px`;
                    modal.style.zIndex = '1050';

                    const modalRect = modal.getBoundingClientRect();
                    const viewportWidth = window.innerWidth;
                    const viewportHeight = window.innerHeight;

                    if (modalRect.right > viewportWidth) {
                        modal.style.left = `${buttonRect.left - modalRect.width - 10}px`;
                    }

                    if (modalRect.bottom > viewportHeight) {
                        modal.style.top = `${viewportHeight - modalRect.height - 10}px`;
                    }

                    if (modalRect.top < 0) {
                        modal.style.top = '10px';
                    }
                }
            }
        };

        updateModalPosition();

        if (showBotResponseModal) {
            window.addEventListener('scroll', updateModalPosition);
            window.addEventListener('resize', updateModalPosition);

            const modal = document.querySelector('.bot-response-modal');
            let resizeObserver;

            if (modal) {
                resizeObserver = new ResizeObserver(updateModalPosition);
                resizeObserver.observe(modal);
            }

            return () => {
                window.removeEventListener('scroll', updateModalPosition);
                window.removeEventListener('resize', updateModalPosition);
                if (resizeObserver) {
                    resizeObserver.disconnect();
                }
            };
        }
    }, [showBotResponseModal]);

    const formatTimestamp = (timestamp) => {
        if (!timestamp) return "N/A";
        const date = new Date(timestamp);
        const formattedDate = date.toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "2-digit",
        });
        const formattedTime = date.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        });
        return `${formattedDate}, ${formattedTime}`;
    };

    const openViewChatModal = (chat) => {
        setSelectedChat(chat);
        setShowViewModal(true);
    };

    const openEditChatModal = (chat, event) => {
        event.stopPropagation();
        setSelectedChat(chat);
        setEditQuery(chat.query || "");
        const responseHtml = chat.customized_response || "";
        setEditResponse(responseHtml);
        editResponseRef.current = responseHtml;
        setEditResponseReady(!!joditPlainText(responseHtml));
        setShowEditModal(true);
    };

    const openAddModal = () => {
        setAddQuery("");
        setAddResponse("");
        addResponseRef.current = "";
        setAddResponseReady(false);
        setShowAddModal(true);
    };

    const openDeleteModal = (chat, event) => {
        event.stopPropagation();
        setSelectedChat(chat);
        setShowDeleteModal(true);
    };

    const handleCloseViewModal = () => {
        setShowViewModal(false);
        setSelectedChat(null);
    };

    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setSelectedChat(null);
        setEditQuery("");
        setEditResponse("");
        editResponseRef.current = "";
        setEditResponseReady(false);
        setShowBotResponseModal(false);
        setBotResponse("");
    };

    const handleCloseAddModal = () => {
        setShowAddModal(false);
        setAddQuery("");
        setAddResponse("");
        addResponseRef.current = "";
        setAddResponseReady(false);
    };

    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
        setSelectedChat(null);
    };

    const handleCloseBotResponseModal = () => {
        setShowBotResponseModal(false);
        setBotResponse("");
    };

    const handleCopyResponse = async () => {
        try {
            await navigator.clipboard.writeText(botResponse);
            success("Response copied to clipboard", true);
        } catch (err) {
            console.error("Error copying response:", err);
            error("Failed to copy response", true);
        }
    };

    const handleAddRecord = async () => {
        console.log("Adding record...");
        if (!siteId) {
            error("Website ID is missing", true);
            return;
        }
        const responseHtml = addResponseRef.current || addResponse;
        if (!addQuery.trim() || !joditPlainText(responseHtml)) {
            error("Query and response are required", true);
            return;
        }

        try {
            setIsSaving(true);
            await axios.post(
                `${getApi}/${siteId}`,
                {
                    query: addQuery,
                    response: responseHtml
                },
                {
                    headers: {
                        "accept": "application/json",
                        "Authorization": "Bearer " + token
                    }
                }
            );
            success("Customized record added successfully", true);
            handleCloseAddModal();
            fetchcustomizedChats(offset.current, searchInputValue);
        } catch (err) {
            console.error("Error adding record:", err);
            error(err.response?.data?.detail || "Failed to add record", true);
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveChanges = async () => {
        if (!selectedChat || !siteId) {
            error("Website ID or record selection is missing", true);
            return;
        }

        const responseHtml = editResponseRef.current || editResponse;
        if (!editQuery.trim() || !joditPlainText(responseHtml)) {
            error("Query and response are required", true);
            return;
        }

        try {
            setIsSaving(true);
            await axios.put(
                `${getApi}/${siteId}`,
                {
                    customized_response_id: selectedChat.customized_response_id,
                    query: editQuery,
                    response: responseHtml
                },
                {
                    headers: {
                        "accept": "application/json",
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + token
                    }
                }
            );
            setCustomizedChats(prev =>
                prev.map(item =>
                    item.customized_response_id === selectedChat.customized_response_id
                        ? { ...item, query: editQuery, customized_response: responseHtml }
                        : item
                )
            );
            success("Record updated successfully", true);
            handleCloseEditModal();
        } catch (err) {
            console.error("Error updating record:", err);
            error(err.response?.data?.detail || "Failed to update record", true);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteResponse = async () => {
        if (!selectedChat || !siteId) {
            error("Website ID or record selection is missing", true);
            return;
        }

        try {
            setIsDeleting(true);
            const id = selectedChat.customized_response_id;
            setCustomizedChats(prev => prev.filter(item => item.customized_response_id !== id));
            await axios.delete(`${getApi}/${siteId}`, {
                headers: {
                    "accept": "application/json",
                    "Authorization": "Bearer " + token
                },
                params: { customized_response_id: id }
            });
            success("Customized record deleted successfully", true);
            handleCloseEditModal();
            handleCloseDeleteModal();
        } catch (err) {
            console.error("Error deleting record:", err);
            error(err.response?.data?.detail || "Failed to delete record", true);
            fetchcustomizedChats(offset.current, searchInputValue);
        } finally {
            setIsDeleting(false);
        }
    };

    const paginate = (pageNumber) => {
        setCurrentPage(pageNumber);
        const newOffset = (pageNumber - 1) * itemsPerPage;
        offset.current = newOffset;
    };

    const handleNextRange = () => {
        if (currentRangeStart + pagesToShow <= totalPages) {
            setCurrentRangeStart(currentRangeStart + pagesToShow);
        }
    };

    const handlePrevRange = () => {
        if (currentRangeStart - pagesToShow > 0) {
            setCurrentRangeStart(currentRangeStart - pagesToShow);
        }
    };

    const visiblePageNumbers = Array.from(
        { length: Math.min(pagesToShow, totalPages - currentRangeStart + 1) },
        (_, index) => currentRangeStart + index
    );

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(parseInt(e.target.value, 10));
        setCurrentPage(1);
        setCurrentRangeStart(1);
    };

    const fetchBotResponseForAdd = async () => {
        if (!siteId || !addQuery.trim()) {
            error("Website ID or query is missing", true);
            return;
        }

        try {
            setIsFetchingBotResponse(true);
            const response = await axios.get(
                `${getBotResponseApi}/${siteId}`,
                {
                    headers: {
                        "accept": "application/json",
                        "Authorization": "Bearer " + token
                    },
                    params: {
                        query: addQuery
                    }
                }
            );

            setAddResponse(response.data || "No response received");
            addResponseRef.current = response.data || "No response received";
            setAddResponseReady(!!joditPlainText(response.data || "No response received"));
            success("Bot response generated successfully", true);
        } catch (err) {
            console.error("Error fetching bot response:", err);
            error(err.response?.data?.detail || "Failed to fetch bot response", true);
        } finally {
            setIsFetchingBotResponse(false);
        }
    };

    return (
        <div className="customize-chat-container" style={{
            fontFamily: '"Inter", sans-serif',
            background: '#f8f9fd',
            margin: 0,
            padding: '20px',
            minHeight: '100vh',
            boxSizing: 'border-box',
            width: '100%',
            maxWidth: '100vw',
            overflowX: 'hidden'
        }}>
            <div style={{
                background: '#fff',
                borderRadius: '16px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                padding: '20px',
                width: '100%',
                boxSizing: 'border-box',
                overflow: 'visible'
            }}>
                {/* Header Section */}
                <div className="header-section">
                    {/* Title Section - Left */}
                    <div className="title-area">
                        <h2 title={`Customized Responses - ${siteName}`}>
                            <span className="d-none d-lg-inline">Customized Responses - </span>
                            {siteName}
                        </h2>
                    </div>

                    {/* Search and Add Section - Centered */}
                    <div className="search-area">
                        <div className="search-input-group">
                            <input
                                type="text"
                                placeholder="Search responses..."
                                className="form-control"
                                value={searchInputValue}
                                onChange={(e) => {
                                    setSearchInputValue(e.target.value);
                                    if (e.target.value.trim() === "") {
                                        setShowClearButton(false);
                                        handleClearSearch();
                                    }
                                }}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        handleSearch();
                                    }
                                }}
                                style={{
                                    paddingRight: '68px',
                                    borderRadius: '8px',
                                    fontSize: '14px'
                                }}
                            />
                            <div style={{
                                position: 'absolute',
                                right: '4px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                display: 'flex',
                                gap: '4px',
                                alignItems: 'center'
                            }}>
                                {showClearButton && (
                                    <Button
                                        variant="outline-secondary"
                                        onClick={handleClearSearch}
                                        style={{
                                            borderRadius: '8px',
                                            width: '28px',
                                            height: '28px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: '0',
                                            cursor: 'pointer',
                                            color: '#5a6acf',
                                            transition: 'all 0.2s ease-in-out',
                                            border: '1px solid #ddd',
                                            backgroundColor: 'white',
                                            boxShadow: '0 1px 1px rgba(0, 0, 0, 0.15)',
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = '#5a6acf';
                                            e.currentTarget.style.color = 'white';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = 'white';
                                            e.currentTarget.style.color = '#5a6acf';
                                        }}
                                    >
                                        <ClearIcon />
                                    </Button>
                                )}
                                <Button
                                    variant="primary"
                                    onClick={handleSearch}
                                    style={{
                                        borderRadius: '8px',
                                        width: '28px',
                                        height: '28px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '0',
                                        cursor: 'pointer',
                                        color: '#5a6acf',
                                        transition: 'all 0.2s ease-in-out',
                                        border: '1px solid #ddd',
                                        backgroundColor: 'white',
                                        boxShadow: '0 1px 1px rgba(0, 0, 0, 0.15)',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = '#5a6acf';
                                        e.currentTarget.style.color = 'white';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = 'white';
                                        e.currentTarget.style.color = '#5a6acf';
                                    }}
                                >
                                    <SearchIcon />
                                </Button>
                            </div>
                        </div>
                        <Button
                            variant="outline-secondary"
                            onClick={openAddModal}
                            style={{
                                background: 'white',
                                border: '1px solid #ddd',
                                borderRadius: '8px',
                                padding: '8px 14px',
                                fontSize: '14px',
                                color: '#5a6acf',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                                flexShrink: 0
                            }}
                        >
                            + Add Record
                        </Button>
                    </div>

                    {/* Action Buttons Section - Right */}
                    <div className="actions-area">
                        <Button
                            variant="outline-secondary"
                            onClick={handleRefresh}
                            style={{
                                background: 'white',
                                border: '1px solid #ddd',
                                borderRadius: '50%',
                                width: '40px',
                                height: '40px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '0',
                                fontSize: '14px',
                                color: '#5a6acf',
                                cursor: 'pointer',
                            }}
                        >
                            <RefreshIcon />
                        </Button>
                        <Button
                            variant="outline-secondary"
                            onClick={() => navigate(-1)}
                            style={{
                                background: 'white',
                                border: '1px solid #ddd',
                                borderRadius: '50%',
                                width: '40px',
                                height: '40px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '0',
                                fontSize: '14px',
                                color: '#5a6acf',
                                cursor: 'pointer',
                                flexShrink: 0
                            }}
                        >
                            <BackIcon />
                        </Button>
                    </div>
                </div>

                {/* Table Container */}
                <div className="table-responsive-custom">
                    <table>
                        <thead>
                            <tr style={{ textAlign: 'left', fontWeight: 600, fontSize: '14px', color: '#555' }}>
                                <th className="col-query">Query</th>
                                <th className="col-response">Response</th>
                                <th className="col-created">Created At</th>
                                <th className="col-actions">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading ? (
                                <tr>
                                    <td colSpan={4} style={{ textAlign: 'center', padding: '14px 10px' }}>
                                        <SectionLoader label="Loading customized chats..." className="revize-section-loader--table" />
                                    </td>
                                </tr>
                            ) : customizedChats.length === 0 ? (
                                <tr>
                                    <td colSpan={4} style={{ textAlign: 'center', padding: '14px 10px', color: '#888' }}>
                                        {searchInputValue ? 'No responses found matching your search.' : 'No customized responses added yet.'}
                                    </td>
                                </tr>
                            ) : (
                            customizedChats.map((chat, index) => (
                                <tr key={index}>
                                    <td onClick={() => openViewChatModal(chat)}>
                                        <div className="cell-content expandable" title={chat.query}>
                                            {chat.query}
                                        </div>
                                    </td>
                                    <td onClick={() => openViewChatModal(chat)}>
                                        <div className="cell-content expandable" title={chat.customized_response ? chat.customized_response.replace(/<[^>]*>?/gm, '') : 'N/A'}>
                                            <ReactMarkdown
                                                rehypePlugins={[rehypeRaw]}
                                                components={{
                                                    p: ({ children }) => <span>{children}</span>,
                                                    ul: ({ children }) => <span>{children}</span>,
                                                    ol: ({ children }) => <span>{children}</span>,
                                                    li: ({ children }) => <span>{children}</span>,
                                                }}
                                            >
                                                {chat.customized_response || 'N/A'}
                                            </ReactMarkdown>
                                        </div>
                                    </td>
                                    <td className="col-created" onClick={() => openViewChatModal(chat)}>
                                        {formatTimestamp(chat.time_stamp)}
                                    </td>
                                    <td className="col-actions">
                                        <div style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            gap: '12px',
                                            alignItems: 'center',
                                            color: '#555'
                                        }}>
                                            <OverlayTrigger placement="top" overlay={<Tooltip>Edit Record</Tooltip>}>
                                                <span
                                                    onClick={(e) => openEditChatModal(chat, e)}
                                                    style={{ cursor: 'pointer', display: 'inline-flex' }}
                                                >
                                                    <EditIcon />
                                                </span>
                                            </OverlayTrigger>

                                            <OverlayTrigger placement="top" overlay={<Tooltip>Discard Customization</Tooltip>}>
                                                <span
                                                    onClick={(e) => openDeleteModal(chat, e)}
                                                    style={{ cursor: 'pointer', display: 'inline-flex' }}
                                                >
                                                    <DeleteIcon />
                                                </span>
                                            </OverlayTrigger>
                                        </div>
                                    </td>
                                </tr>
                            ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {!isLoading && customizedChats.length > 0 && (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: '15px',
                        fontSize: '14px',
                        color: '#888',
                        flexWrap: 'wrap',
                        gap: '15px',
                        width: '100%'
                    }}>
                        <div style={{
                            flex: '1 1 200px',
                            textAlign: 'left',
                            minWidth: 'min-content'
                        }}>
                            <span>Showing data {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} entries</span>
                        </div>

                        <div style={{
                            flex: '1 1 auto',
                            display: 'flex',
                            justifyContent: 'center',
                            gap: '6px',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            minWidth: 'min-content'
                        }}>
                            <button
                                onClick={handlePrevRange}
                                disabled={currentRangeStart === 1}
                                style={{
                                    padding: '4px 8px',
                                    border: '1px solid #ddd',
                                    borderRadius: '6px',
                                    background: '#fff',
                                    cursor: currentRangeStart === 1 ? 'not-allowed' : 'pointer',
                                    fontSize: '13px',
                                    opacity: currentRangeStart === 1 ? 0.5 : 1,
                                    flexShrink: 0
                                }}
                            >
                                &lt;
                            </button>

                            {visiblePageNumbers.map((number) => (
                                <button
                                    key={number}
                                    onClick={() => paginate(number)}
                                    style={{
                                        padding: '4px 12px',
                                        border: '1px solid #ddd',
                                        borderRadius: '6px',
                                        background: currentPage === number ? '#5A6ACF' : '#fff',
                                        color: currentPage === number ? '#fff' : 'inherit',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        flexShrink: 0
                                    }}
                                >
                                    {number}
                                </button>
                            ))}

                            {currentRangeStart + pagesToShow <= totalPages && (
                                <span style={{ flexShrink: 0 }}>...</span>
                            )}

                            <button
                                onClick={handleNextRange}
                                disabled={currentRangeStart + pagesToShow > totalPages}
                                style={{
                                    padding: '4px 8px',
                                    border: '1px solid #ddd',
                                    borderRadius: '6px',
                                    background: '#fff',
                                    cursor: currentRangeStart + pagesToShow > totalPages ? 'not-allowed' : 'pointer',
                                    fontSize: '13px',
                                    opacity: currentRangeStart + pagesToShow > totalPages ? 0.5 : 1,
                                    flexShrink: 0
                                }}
                            >
                                &gt;
                            </button>
                        </div>

                        <div style={{
                            flex: '1 1 200px',
                            textAlign: 'right',
                            minWidth: 'min-content'
                        }}>
                            <label htmlFor="itemsPerPage" style={{ marginRight: '10px' }}>Items per page:</label>
                            <select
                                id="itemsPerPage"
                                value={itemsPerPage}
                                onChange={handleItemsPerPageChange}
                                style={{
                                    padding: '6px 12px',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    height: '32px',
                                    width: '80px',
                                    fontSize: '13px',
                                    appearance: 'menulist'
                                }}
                            >
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                                <option value={150}>150</option>
                                <option value={200}>200</option>
                                <option value={500}>500</option>
                                <option value={1000}>1000</option>
                            </select>
                        </div>
                    </div>
                )}
            </div>

            {/* All modals remain the same */}
            {selectedChat && (
                <Modal show={showViewModal} onHide={handleCloseViewModal} size="xl">
                    <Modal.Header closeButton>
                        <Modal.Title>Customized Response Details</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="mt-3">
                            <h5>Created At:</h5>
                            <pre style={{
                                backgroundColor: '#f4f4f4',
                                padding: '10px',
                                borderRadius: '5px',
                                maxHeight: '300px',
                                overflowY: 'auto',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word'
                            }}>
                                {formatTimestamp(selectedChat.time_stamp)}
                            </pre>
                        </div>
                        <div className="mt-3">
                            <h5>Query:</h5>
                            <pre style={{
                                backgroundColor: '#f4f4f4',
                                padding: '10px',
                                borderRadius: '5px',
                                maxHeight: '300px',
                                overflowY: 'auto',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word'
                            }}>
                                {selectedChat.query || 'N/A'}
                            </pre>
                        </div>
                        <div className="mt-3">
                            <h5>Response:</h5>
                            <pre style={{
                                backgroundColor: '#f4f4f4',
                                padding: '10px',
                                borderRadius: '5px',
                                maxHeight: '300px',
                                overflowY: 'auto',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word'
                            }}>
                                <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                                    {selectedChat.customized_response || 'N/A'}
                                </ReactMarkdown>
                            </pre>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="secondary"
                            onClick={handleCloseViewModal}
                            style={{ border: '1px solid #ddd' }}
                        >
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}

            {/* Edit Modal */}
            {selectedChat && (
                <Modal show={showEditModal} onHide={handleCloseEditModal} size="xl">
                    <Modal.Header closeButton>
                        <Modal.Title>Edit Customized Response</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label>Query</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={editQuery}
                                    onChange={(e) => setEditQuery(e.target.value)}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <div className="d-flex align-items-center">
                                    <Form.Label className="mb-0">Customized Response</Form.Label>
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip>Get Bot Response</Tooltip>}
                                    >
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={fetchBotResponse}
                                            disabled={isFetchingBotResponse || !editQuery.trim()}
                                            className="ms-2"
                                            aria-label="Get Bot Response"
                                            ref={botResponseButtonRef}
                                        >
                                            {isFetchingBotResponse ? (
                                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                            ) : (
                                                <svg
                                                    width="1.3rem"
                                                    height="1.3rem"
                                                    viewBox="0 0 24 26"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="h-2/3 w-2/3"
                                                >
                                                    <text x="-9999" y="-9999">ChatGPT</text>
                                                    <path
                                                        d="M9.20509 8.76511V6.50545C9.20509 6.31513 9.27649 6.17234 9.44293 6.0773L13.9861 3.46088C14.6046 3.10413 15.342 2.93769 16.103 2.93769C18.9573 2.93769 20.7651 5.14983 20.7651 7.50454C20.7651 7.67098 20.7651 7.86129 20.7412 8.05161L16.0316 5.2924C15.7462 5.12596 15.4607 5.12596 15.1753 5.2924L9.20509 8.76511ZM19.8135 17.5659V12.1664C19.8135 11.8333 19.6708 11.5955 19.3854 11.429L13.4152 7.95633L15.3656 6.83833C15.5321 6.74328 15.6749 6.74328 15.8413 6.83833L20.3845 9.45474C21.6928 10.216 22.5728 11.8333 22.5728 13.4031C22.5728 15.2108 21.5025 16.8758 19.8135 17.5657V17.5659ZM7.80173 12.8088L5.8513 11.6671C5.68486 11.5721 5.61346 11.4293 5.61346 11.239V6.00613C5.61346 3.46111 7.56389 1.53433 10.2042 1.53433C11.2033 1.53433 12.1307 1.86743 12.9159 2.46202L8.2301 5.17371C7.94475 5.34015 7.80195 5.57798 7.80195 5.91109V12.809L7.80173 12.8088ZM12 15.2349L9.20509 13.6651V10.3351L12 8.76534L14.7947 10.3351V13.6651L12 15.2349ZM13.7958 22.4659C12.7967 22.4659 11.8693 22.1328 11.0841 21.5382L15.7699 18.8265C16.0553 18.6601 16.198 18.4222 16.198 18.0891V11.1912L18.1723 12.3329C18.3388 12.4279 18.4102 12.5707 18.4102 12.761V17.9939C18.4102 20.5389 16.4359 22.4657 13.7958 22.4657V22.4659ZM8.15848 17.1617L3.61528 14.5452C2.30696 13.784 1.42701 12.1667 1.42701 10.5969C1.42701 8.76534 2.52115 7.12414 4.20987 6.43428V11.8574C4.20987 12.1905 4.35266 12.4284 4.63802 12.5948L10.5846 16.0436L8.63415 17.1617C8.46771 17.2567 8.32492 17.2567 8.15848 17.1617ZM7.897 21.0625C5.20919 21.0625 3.23488 19.0407 3.23488 16.5432C3.23488 16.3529 3.25875 16.1626 3.2824 15.9723L7.96817 18.6839C8.25352 18.8504 8.53911 18.8504 8.82446 18.6839L14.7947 15.2351V17.4948C14.7947 17.6851 14.7233 17.8279 14.5568 17.9229L10.0136 20.5393C9.39518 20.8961 8.6578 21.0625 7.89677 21.0625H7.897ZM13.7958 23.8929C16.6739 23.8929 19.0762 21.8474 19.6235 19.1357C22.2874 18.4459 24 15.9484 24 13.4034C24 11.7383 23.2865 10.121 22.002 8.95542C22.121 8.45588 22.1924 7.95633 22.1924 7.45702C22.1924 4.0557 19.4331 1.51045 16.2458 1.51045C15.6037 1.51045 14.9852 1.60549 14.3668 1.81968C13.2963 0.773071 11.8215 0.107086 10.2042 0.107086C7.32606 0.107086 4.92383 2.15256 4.37653 4.86425C1.7126 5.55411 0 8.05161 0 10.5966C0 12.2617 0.713506 13.879 1.99795 15.0446C1.87904 15.5441 1.80764 16.0436 1.80764 16.543C1.80764 19.9443 4.56685 22.4895 7.75421 22.4895C8.39632 22.4895 9.01478 22.3945 9.63324 22.1803C10.7035 23.2269 12.1783 23.8929 13.7958 23.8929Z"
                                                        fill="currentColor"
                                                    />
                                                </svg>
                                            )}
                                        </Button>
                                    </OverlayTrigger>
                                </div>
                                <div className="html-editor-container mt-2">
                                    <JoditEditor
                                        value={editResponse}
                                        config={joditConfig}
                                        tabIndex={1}
                                        onChange={(newContent) => syncEditorContent(newContent, "edit")}
                                        onBlur={(newContent) => {
                                            syncEditorContent(newContent, "edit");
                                            setEditResponse(newContent);
                                        }}
                                    />
                                </div>
                            </Form.Group>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer className="d-flex justify-content-between">
                        <div>
                            <Button
                                variant="secondary"
                                onClick={handleCloseEditModal}
                                disabled={isSaving || isDeleting}
                                className="me-2"
                                style={{ border: '1px solid #ddd', minWidth: '100px' }}
                            >
                                Close
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleSaveChanges}
                                disabled={isSaving || isDeleting || !editQuery.trim() || !editResponseReady}
                                style={{
                                    backgroundColor: '#5a6acf',
                                    borderColor: '#5a6acf',
                                    minWidth: '100px'
                                }}
                            >
                                {isSaving ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                        Saving
                                    </>
                                ) : (
                                    "Save"
                                )}
                            </Button>
                        </div>
                    </Modal.Footer>
                </Modal>
            )}

            {/* Bot Response Modal */}
            {showBotResponseModal && (
                <Modal
                    show={showBotResponseModal}
                    onHide={handleCloseBotResponseModal}
                    size="lg"
                    dialogClassName="bot-response-modal"
                    backdrop={false}
                    style={{ position: 'fixed' }}
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Bot Response</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <pre style={{
                            backgroundColor: '#f4f4f4',
                            padding: '10px',
                            borderRadius: '5px',
                            maxHeight: '300px',
                            overflowY: 'auto',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word'
                        }}>
                            <ReactMarkdown rehypePlugins={[rehypeRaw]}>
                                {botResponse || 'No response available'}
                            </ReactMarkdown>
                        </pre>
                    </Modal.Body>
                    <Modal.Footer className="d-flex justify-content-between align-items-center">
                        <div className="d-flex gap-2">
                            <Button
                                variant="outline-primary"
                                onClick={handleCopyResponse}
                                size="sm"
                            >
                                Copy
                            </Button>
                            <Button
                                variant="outline-primary"
                                onClick={fetchBotResponse}
                                disabled={isFetchingBotResponse}
                                size="sm"
                            >
                                {isFetchingBotResponse ? (
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                ) : (
                                    "Regenerate"
                                )}
                            </Button>
                        </div>
                        <Button
                            variant="secondary"
                            onClick={handleCloseBotResponseModal}
                            size="sm"
                            style={{ border: '1px solid #ddd' }}
                        >
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}

            {/* Delete Modal */}
            {selectedChat && (
                <Modal show={showDeleteModal} onHide={handleCloseDeleteModal}>
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm Deletion</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        Are you sure to delete this customization?
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            variant="secondary"
                            onClick={handleCloseDeleteModal}
                            disabled={isDeleting}
                            style={{ border: '1px solid #ddd' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            onClick={handleDeleteResponse}
                            disabled={isDeleting}
                        >
                            {isDeleting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Deleting
                                </>
                            ) : (
                                "Delete customization"
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}

            {/* Add Modal */}
            <Modal show={showAddModal} onHide={handleCloseAddModal} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>Add Customized Response</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Query</Form.Label>
                            <Form.Control
                                type="text"
                                value={addQuery}
                                onChange={(e) => setAddQuery(e.target.value)}
                                placeholder="Enter query"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <div className="d-flex align-items-center">
                                <Form.Label className="mb-0">Response</Form.Label>
                                <OverlayTrigger
                                    placement="top"
                                    overlay={<Tooltip>Get Bot Response</Tooltip>}
                                >
                                    <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={fetchBotResponseForAdd}
                                        disabled={isFetchingBotResponse || !addQuery.trim()}
                                        className="ms-2"
                                        aria-label="Get Bot Response"
                                    >
                                        {isFetchingBotResponse ? (
                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                        ) : (
                                            <svg
                                                width="1.3rem"
                                                height="1.3rem"
                                                viewBox="0 0 24 26"
                                                fill="none"
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="h-2/3 w-2/3"
                                            >
                                                <text x="-9999" y="-9999">ChatGPT</text>
                                                <path
                                                    d="M9.20509 8.76511V6.50545C9.20509 6.31513 9.27649 6.17234 9.44293 6.0773L13.9861 3.46088C14.6046 3.10413 15.342 2.93769 16.103 2.93769C18.9573 2.93769 20.7651 5.14983 20.7651 7.50454C20.7651 7.67098 20.7651 7.86129 20.7412 8.05161L16.0316 5.2924C15.7462 5.12596 15.4607 5.12596 15.1753 5.2924L9.20509 8.76511ZM19.8135 17.5659V12.1664C19.8135 11.8333 19.6708 11.5955 19.3854 11.429L13.4152 7.95633L15.3656 6.83833C15.5321 6.74328 15.6749 6.74328 15.8413 6.83833L20.3845 9.45474C21.6928 10.216 22.5728 11.8333 22.5728 13.4031C22.5728 15.2108 21.5025 16.8758 19.8135 17.5657V17.5659ZM7.80173 12.8088L5.8513 11.6671C5.68486 11.5721 5.61346 11.4293 5.61346 11.239V6.00613C5.61346 3.46111 7.56389 1.53433 10.2042 1.53433C11.2033 1.53433 12.1307 1.86743 12.9159 2.46202L8.2301 5.17371C7.94475 5.34015 7.80195 5.57798 7.80195 5.91109V12.809L7.80173 12.8088ZM12 15.2349L9.20509 13.6651V10.3351L12 8.76534L14.7947 10.3351V13.6651L12 15.2349ZM13.7958 22.4659C12.7967 22.4659 11.8693 22.1328 11.0841 21.5382L15.7699 18.8265C16.0553 18.6601 16.198 18.4222 16.198 18.0891V11.1912L18.1723 12.3329C18.3388 12.4279 18.4102 12.5707 18.4102 12.761V17.9939C18.4102 20.5389 16.4359 22.4657 13.7958 22.4657V22.4659ZM8.15848 17.1617L3.61528 14.5452C2.30696 13.784 1.42701 12.1667 1.42701 10.5969C1.42701 8.76534 2.52115 7.12414 4.20987 6.43428V11.8574C4.20987 12.1905 4.35266 12.4284 4.63802 12.5948L10.5846 16.0436L8.63415 17.1617C8.46771 17.2567 8.32492 17.2567 8.15848 17.1617ZM7.897 21.0625C5.20919 21.0625 3.23488 19.0407 3.23488 16.5432C3.23488 16.3529 3.25875 16.1626 3.2824 15.9723L7.96817 18.6839C8.25352 18.8504 8.53911 18.8504 8.82446 18.6839L14.7947 15.2351V17.4948C14.7947 17.6851 14.7233 17.8279 14.5568 17.9229L10.0136 20.5393C9.39518 20.8961 8.6578 21.0625 7.89677 21.0625H7.897ZM13.7958 23.8929C16.6739 23.8929 19.0762 21.8474 19.6235 19.1357C22.2874 18.4459 24 15.9484 24 13.4034C24 11.7383 23.2865 10.121 22.002 8.95542C22.121 8.45588 22.1924 7.95633 22.1924 7.45702C22.1924 4.0557 19.4331 1.51045 16.2458 1.51045C15.6037 1.51045 14.9852 1.60549 14.3668 1.81968C13.2963 0.773071 11.8215 0.107086 10.2042 0.107086C7.32606 0.107086 4.92383 2.15256 4.37653 4.86425C1.7126 5.55411 0 8.05161 0 10.5966C0 12.2617 0.713506 13.879 1.99795 15.0446C1.87904 15.5441 1.80764 16.0436 1.80764 16.543C1.80764 19.9443 4.56685 22.4895 7.75421 22.4895C8.39632 22.4895 9.01478 22.3945 9.63324 22.1803C10.7035 23.2269 12.1783 23.8929 13.7958 23.8929Z"
                                                    fill="currentColor"
                                                />
                                            </svg>
                                        )}
                                    </Button>
                                </OverlayTrigger>
                            </div>
                            <div className="html-editor-container mt-2">
                                <JoditEditor
                                    value={addResponse}
                                    config={joditConfig}
                                    tabIndex={1}
                                    onChange={(newContent) => syncEditorContent(newContent, "add")}
                                    onBlur={(newContent) => {
                                        syncEditorContent(newContent, "add");
                                        setAddResponse(newContent);
                                    }}
                                />
                            </div>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={handleCloseAddModal}
                        disabled={isSaving}
                        style={{ border: '1px solid #ddd', minWidth: '100px' }}
                    >
                        Close
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleAddRecord}
                        disabled={isSaving || !addQuery.trim() || !addResponseReady}
                        style={{
                            backgroundColor: '#5a6acf',
                            borderColor: '#5a6acf',
                            minWidth: '100px'
                        }}
                    >
                        {isSaving ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Saving
                            </>
                        ) : (
                            "Save"
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default CustomizeChat;
